// WhistleBlowChannelEditDialogUserList - MODULE
// ***********************************************************************************************************************
import { Alert, Box, Typography } from "@mui/material";
import * as React from "react";
import { AvatarImage } from "ui/Scripts/source/components/framework/Avatar/AvatarImage";
import { Translations } from "ui/Scripts/source/models/translations";
import { Base } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/framework/base";
import { IModifyUser, ModifyUser } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/models/user/modifyUser";
import { AppUtils } from "../../../framework/appUtils";
import { SaveDataObj } from "../../../framework/saveData";
import * as whistleBlowService from "../../../models/services/whistleBlowService";
import { ISessionState } from "../../../models/session/sessionState";
import { IUserListItem } from "../../../models/user/userListItem";
import { ActionContainer } from "../../framework/ActionContainer";
import { DialogSettingsCard } from "../../framework/Container/DialogSettingsCard";
import { List } from "../../framework/List/List";
import { ListSearchFilter } from "../../framework/List/ListSearchFilter";
import { SelectUserDialog } from "../../user/SelectUserDialog/SelectUserDialog";
import { AddUserToolButton } from "source/components/framework/Button/AddUserToolButton";
import { RemoveUserToolButton } from "source/components/framework/Button/RemoveUserToolButton";

// WhistleBlowChannelEditDialogUserList
export interface IWhistleBlowChannelEditDialogUserListProp {
    isReadOnly?: boolean;
    sessionState: ISessionState;
    whistleBlowChannelId: string;
    companyId: string;
    title: string;
    users: IModifyUser[];
    onRemoved: (ids: string[]) => void;
    onAdded: (users: IModifyUser[]) => void;
    onRefreshSessionState: () => void;
}

interface IWhistleBlowChannelEditDialogUserListState {
    // List
    isLoading: boolean;
    users: IModifyUser[];
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    selectedId: string;
    checkedIds: string[];

    // Dialog
    showSelectUserDialog: boolean;
}

export class WhistleBlowChannelEditDialogUserList extends React.Component<IWhistleBlowChannelEditDialogUserListProp, IWhistleBlowChannelEditDialogUserListState> {
    constructor(props: IWhistleBlowChannelEditDialogUserListProp) {
        super(props);
        this.state = {
            isLoading: false,
            users: props.users ? props.users.slice(0) : [],
            filter: "",
            sortColumn: "code",
            sortOrderIsAsc: true,
            selectedId: null,
            checkedIds: [],
            showSelectUserDialog: false,
        };
    }

    filteredList = (filter: string, column: string, asc: boolean) => {
        const props = this.props;
        let users = props.users ? props.users.slice(0) : [];
        if (filter) {
            const lowFilter = filter.toLowerCase();
            users = users.filter(i => i.code.toLowerCase().indexOf(lowFilter) > -1 || i.name.toLowerCase().indexOf(lowFilter) > -1);
        }
        ModifyUser.sortModifyUsers(users, { column, asc });
        this.setState({
            filter,
            sortColumn: column,
            sortOrderIsAsc: asc,
            users,
        });
    };

    componentDidUpdate(prevProps: IWhistleBlowChannelEditDialogUserListProp, prevState: IWhistleBlowChannelEditDialogUserListState) {
        const props = this.props;
        const state = this.state;
        if (Base.equalStrArrays(prevProps.users ? prevProps.users.map(i => i.code) : [], props.users ? props.users.map(i => i.code) : [])) return;
        this.filteredList(state.filter, state.sortColumn, state.sortOrderIsAsc);
    }

    changeFilter = (filter: string) => {
        const state = this.state;
        this.filteredList(filter, state.sortColumn, state.sortOrderIsAsc);
    };

    handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        const state = this.state;
        this.filteredList(state.filter, sortColumn, sortOrderIsAsc);
    };

    handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        this.setState({
            selectedId,
            checkedIds
        });
    };

    getRemoveSaveData = (props: IWhistleBlowChannelEditDialogUserListProp, userCodes: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", props.whistleBlowChannelId);
        result.append("companyId", props.companyId);
        result.append("userCodes", userCodes);
        return result;
    };

    handleRemove = async () => {
        const props = this.props;
        const state = this.state;
        const selectedIds = List.getSelectedIds(state.selectedId, state.checkedIds).filter(i => i !== props.sessionState.code);
        if (selectedIds.length < 1) return;
        const users = props.users.filter(i => selectedIds.indexOf(i.code) < 0);
        if (users.length < 1) {
            AppUtils.showErrorMessage(Translations.WhistleBlowChannelMustHaveAtLeastOneAdministrator);
            return;
        }
        const saveData = this.getRemoveSaveData(props, selectedIds);
        // Call server
        const result = await AppUtils.callService(() => whistleBlowService.whistleChannelRemoveAdmins(saveData.obj));
        if (!result) return;
        props.onRemoved(selectedIds);
        if (result.refreshSessionState) {
            props.onRefreshSessionState();
        }
    };
    
    handleAdd = () => {
        this.setState({
            showSelectUserDialog: true
        });
    };

    getAddSaveData = (props: IWhistleBlowChannelEditDialogUserListProp, userCodes: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", props.whistleBlowChannelId);
        result.append("companyId", props.companyId);
        result.append("userCodes", userCodes);
        return result;
    };

    onSelectUserDialogOk = async (users: IUserListItem[]) => {
        const props = this.props;
        if (!users || users.length < 1) return;
        this.setState({
            showSelectUserDialog: false
        });
        const saveData = this.getAddSaveData(this.props, users.map(i => i.code));
        // Call server
        const result = await AppUtils.callService(() => whistleBlowService.whistleChannelAddAdmins(saveData.obj));
        if (!result) return;
        props.onAdded(users.map(i => ModifyUser.createModifyUser(i.code, i.getName(), 0, i.picture)));
        if (result.refreshSessionState) {
            props.onRefreshSessionState();
        }
    };

    onSelectUserDialogCancel = () => {
        this.setState({
            showSelectUserDialog: false
        });
    };

    render() {
        const props = this.props;
        const state = this.state;
        const removableUserCodes = List.getSelectedIds(state.selectedId, state.checkedIds).filter(i => i !== props.sessionState.code);
        return (
            <>
                <DialogSettingsCard
                    fitToContainerHeight
                    title={props.title}
                    cardContent={
                        <ActionContainer
                            leftItems={
                                <ListSearchFilter
                                    searchFilter={state.filter}
                                    onSearchClick={this.changeFilter} />
                            }
                            rightItems={
                                <>
                                    <AddUserToolButton
                                        disabled={props.isReadOnly}
                                        tooltip={Translations.AddUser}
                                        onClick={this.handleAdd}
                                    />
                                    <RemoveUserToolButton
                                        tooltip={Translations.RemoveUser}
                                        disabled={removableUserCodes.length < 1}
                                        onClick={this.handleRemove}
                                    />
                                </>
                            }
                        />
                    }
                    content={
                        <>
                            {!!props.isReadOnly &&
                                <Alert severity="info">{Translations.NewUserGroupMustBeSavedBeforeUsersCanBeAdded}</Alert>
                            }
                            {!props.isReadOnly &&
                                <List
                                    height="100%"
                                    intialized={true}
                                    isLoading={state.isLoading}
                                    hasMore={false}
                                    globalLoadingInUse={false}
                                    header={{
                                        columns: [
                                            { size: 6, column: "code", content: Translations.UserName },
                                            { size: 6, column: "name", content: Translations.Name }
                                        ]
                                    }}
                                    lines={
                                        state.users.map((user) => {
                                            return {
                                                columns: [
                                                    {
                                                        size: 6,
                                                        column: "code",
                                                        content:
                                                    <Box display="flex" alignItems="center">
                                                        <AvatarImage
                                                            id={user.code}
                                                            name={user.name}
                                                            image={user.picture}
                                                            tooltip={user.name}
                                                        />
                                                        <Typography sx={{ marginLeft: ".5rem" }}>{user.code}</Typography>
                                                    </Box>
                                                    },
                                                    { size: 6, column: "name", content: user.name }
                                                ],
                                                id: user.code
                                            };
                                        })
                                    }
                                    noDataMessage={Translations.AddUsers}
                                    sortColumn={state.sortColumn}
                                    sortOrderIsAsc={state.sortOrderIsAsc}
                                    selectedId={state.selectedId}
                                    checkedIds={state.checkedIds}
                                    onSetSelectedIds={this.handleSetSelectedIds}
                                    onSearchLines={this.handleSearchLines}
                                />
                            }
                        </>
                    }
                />
                {state.showSelectUserDialog &&
                    <SelectUserDialog
                        companyId={props.companyId}
                        multiSelect={true}
                        ignoreUserCodes={props.users.map(i => i.code)}
                        ignoreQrCodeUsers={true}
                        onOk={this.onSelectUserDialogOk}
                        onCancel={this.onSelectUserDialogCancel}
                    />
                }
            </>
        );
    }
}